// useCamera — shared local/remote media logic for the online (live whiteboard)
// Host and Guest views (P3-12 step 2). Owns device enumeration, mic/camera
// permission acquisition, the combined local MediaStream, mic/camera toggles,
// stream cleanup, and the <video> srcObject wiring.
//
// Role-specific bits stay in the consuming view:
//   - `handleGesture()` (Host adds tracks to WebRTC; Guest opens the socket)
//   - `gestured`, `webrtc` (session/lifecycle state — extracted later in step 4)
//
// The mixin references `this.gestured`, `this.webrtc`, and `this.handleGesture`,
// which each view provides. Vue merges this mixin's data/computed/methods/watch
// with the component's; on a key clash the component wins, so the view keeps
// these keys out of its own `data()` to avoid shadowing.
export default {
  data() {
    return {
      // Local/remote media streams and the <video> re-mount keys
      localStream: null,
      localVideoKey: 0,
      remoteStream: null,
      remoteVideoKey: 0,

      // Mute/camera UI state
      cameraEnabled: false,
      micMuted: true,
      remoteMuted: true,

      // Raw per-kind streams captured at permission time
      audioStream: null,
      videoStream: null,

      // Permission flags
      audioPermissionGranted: false,
      videoPermissionGranted: false,
      audioPermissionDenied: false,
      videoPermissionDenied: false,

      // Device availability
      hasAudioDevice: false,
      hasVideoDevice: false,
    }
  },

  computed: {
    // Helper computed properties for UI
    canUseMicrophone() {
      return this.hasAudioDevice && !this.audioPermissionDenied
    },

    canUseCamera() {
      return this.hasVideoDevice && !this.videoPermissionDenied
    },

    microphoneReady() {
      return this.audioPermissionGranted && this.audioStream
    },

    cameraReady() {
      return this.videoPermissionGranted && this.videoStream
    },
  },

  watch: {
    localStream(newStream) {
      if (this.$refs.vp && this.$refs.vp instanceof HTMLVideoElement) {
        this.$refs.vp.srcObject = newStream
      }
    },
    remoteStream(newStream) {
      console.log('Remote stream updated', newStream)
      if (this.$refs.vpremote && this.$refs.vpremote instanceof HTMLVideoElement) {
        this.$refs.vpremote.srcObject = newStream
      }
    },
  },

  methods: {
    // Check available devices
    async checkAvailableDevices() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        this.hasAudioDevice = devices.some(device => device.kind === 'audioinput')
        this.hasVideoDevice = devices.some(device => device.kind === 'videoinput')

        console.log('Available devices:', {
          audio: this.hasAudioDevice,
          video: this.hasVideoDevice,
        })
      } catch (error) {
        console.error('Error checking devices:', error)
      }
    },

    // Request audio permission separately
    async requestAudioPermission() {
      if (!this.hasAudioDevice) {
        console.log('No audio device available')
        return false
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        })

        this.audioStream = stream
        this.audioPermissionGranted = true
        this.audioPermissionDenied = false

        // Set initial muted state
        stream.getAudioTracks().forEach(track => {
          // eslint-disable-next-line no-param-reassign
          track.enabled = !this.micMuted
        })

        console.log('Audio permission granted')
        return true
      } catch (error) {
        console.error('Audio permission denied:', error)
        this.audioPermissionDenied = true
        this.audioPermissionGranted = false
        return false
      }
    },

    // Request video permission separately
    async requestVideoPermission() {
      if (!this.hasVideoDevice) {
        console.log('No video device available')
        return false
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: true,
        })

        this.videoStream = stream
        this.videoPermissionGranted = true
        this.videoPermissionDenied = false

        // Set initial camera state
        stream.getVideoTracks().forEach(track => {
          // eslint-disable-next-line no-param-reassign
          track.enabled = this.cameraEnabled
        })

        console.log('Video permission granted')
        return true
      } catch (error) {
        console.error('Video permission denied:', error)
        this.videoPermissionDenied = true
        this.videoPermissionGranted = false
        return false
      }
    },

    // Combine streams for WebRTC
    combineStreams() {
      const tracks = []

      if (this.audioStream) {
        tracks.push(...this.audioStream.getAudioTracks())
      }

      if (this.videoStream) {
        tracks.push(...this.videoStream.getVideoTracks())
      }

      if (tracks.length > 0) {
        console.log('Combined stream created with tracks:', tracks.length)
        return new MediaStream(tracks)
      }

      return null
    },

    // Modified mic toggle - only affects audio stream
    handleMicToggle(state) {
      this.micMuted = !state

      if (!this.gestured) {
        if (!this.localStream) {
          this.handleGesture(false)
        }
      }

      // Also update local stream if it exists
      if (this.localStream) {
        this.localStream.getAudioTracks().forEach(track => {
          // eslint-disable-next-line no-param-reassign
          track.enabled = state
        })
      }

      if (this.gestured && this.webrtc) {
        this.webrtc.sendMicToggle(state)
      }
    },
    handleCameraToggle(state) {
      this.cameraEnabled = state

      if (!this.gestured) {
        if (!this.localStream) {
          this.handleGesture(false)
        }
      }

      if (this.localStream) {
        this.localStream.getVideoTracks().forEach(track => {
          // eslint-disable-next-line no-param-reassign
          track.enabled = state
        })
      }

      if (this.gestured && this.webrtc) {
        this.webrtc.sendCameraToggle(state)
      }
    },

    // Request specific permission when user clicks mic/camera buttons
    async requestSpecificPermission(type) {
      if (type === 'audio') {
        return this.requestAudioPermission()
      } if (type === 'video') {
        return this.requestVideoPermission()
      }
      return false
    },

    // Clean up streams
    cleanupStreams() {
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop())
        this.audioStream = null
      }

      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => track.stop())
        this.videoStream = null
      }

      if (this.localStream) {
        this.localStream = null
      }
    },
  },
}
